import { gql, useLazyQuery, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import Button from '@mui/material/Button';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import deleteItemCollection from 'src/api/collection/deleteItemCollection';
import deleteWholeCollection from 'src/api/collection/deleteWholeCollection';
import showMyCollections from 'src/api/collection/showMyCollections';
import CustomLinearProgress from 'src/components/loading/CustomLinearProgress';
import { StyledContextMenu } from 'src/components/menus/StyledContextMenu';
import type { ActionItem } from 'src/constants';
import {
  CLEAR,
  COLLECTION,
  CREATE_GALLERY,
  DELETE,
  DOWNLOAD,
  HOME_PATH,
  PLAYLIST_ACTIONS,
  PREVIEW,
  RENAME,
  SHARE,
  USER_PERMISSONS_TO_PLAYLIST_ACTIONS_MAP,
} from 'src/constants';
import { type DownloadLink, DownloadManager, queueAndDownload } from 'src/download';
import { Collection } from 'src/dynamoQueries/collections';
import { buildDownloadLinksQ } from 'src/dynamoQueries/download';
import CreateGallerySwitch from 'src/Gallery/CreateGallerySwitch';
import { getCollectionItemGlobalIds } from 'src/helpers';
import { gatherActionsFromPerms } from 'src/helpers/permissionsHelper';
import { useCustomToast } from 'src/hooks/menu';
import { useMyPerms } from 'src/hooks/perms';
import { onDownloadLinkErrorMsg } from 'src/messages/error';
import viewStateVar from 'src/store/viewState';

const useStyles = makeStyles((_theme: Theme) => ({
  actionsButtonContainer: {
    zIndex: 2000,
    cursor: 'pointer',
  },
  actionsButton: {
    minWidth: 0,
  },
}));

interface FormatNode {
  id: string;
  online?: boolean;
}

interface FormatEdge {
  node: FormatNode;
}

interface FormatSet {
  edges: FormatEdge[];
}

interface ContentObject {
  id: string;
  name?: string;
  globalId?: string;
  formatSet: FormatSet;
}

interface CollectionItemNode {
  contentObject: ContentObject;
}

interface CollectionItemEdge {
  node: CollectionItemNode;
}

interface CollectionQueryData {
  collection?: {
    canShare?: boolean;
  };
}

interface DownloadLinksData {
  downloadLinks: DownloadLink[];
}

interface UserPermissions {
  userPerms?: Record<string, boolean>;
}

interface MyPermissionsData {
  myPermissions?: UserPermissions;
}

interface ActionsButtonProps {
  collectionItems: CollectionItemEdge[];
}

const ActionsButton: React.FC<ActionsButtonProps> = ({ collectionItems }) => {
  const classes = useStyles();
  const viewState = useReactiveVar(viewStateVar);

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [openGalleryModal, setOpenGalleryModal] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { toastr: cToastr } = useCustomToast();

  const [deleteItemsHandler] = useMutation(deleteItemCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const [getDownloadLinks] = useLazyQuery<DownloadLinksData>(buildDownloadLinksQ());
  const {
    data: perms,
    loading: permsLoading,
    error: permsError,
  } = useMyPerms() as {
    data: MyPermissionsData | undefined;
    loading: boolean;
    error: Error | undefined;
  };

  const buildCollectionShareQuery = (): ReturnType<typeof gql> => {
    const collection = Collection(true);
    collection.query.collection.canShare = true;

    delete (collection.query as Record<string, unknown>).__variables;
    collection.query.__variables = { id: 'ID!' };
    const gqlQuery = jsonToGraphQLQuery(collection);
    return gql(gqlQuery);
  };

  const {
    data: collectionData,
    loading: collectionLoading,
    error: collectionError,
  } = useQuery<CollectionQueryData>(buildCollectionShareQuery(), {
    fetchPolicy: 'network-only',
    variables: {
      id: viewState.selectedCollectionId,
    },
  });

  const getActions = (): ActionItem[] => {
    const actions: ActionItem[] = [...PLAYLIST_ACTIONS];
    const canShare = collectionData?.collection?.canShare || false;
    const userPermActions = gatherActionsFromPerms(
      perms?.myPermissions?.userPerms,
      USER_PERMISSONS_TO_PLAYLIST_ACTIONS_MAP,
    );

    if (!canShare) {
      const shareIndex = actions.findIndex((action) => action.label === SHARE);
      if (shareIndex !== -1) {
        actions.splice(shareIndex, 1);
      }
    }
    return [...actions, ...(userPermActions as ActionItem[])];
  };

  const [deleteCollectionHandler] = useMutation(deleteWholeCollection, {
    variables: { collectionId: viewState.selectedCollectionId },
    fetchPolicy: 'network-only',
    onCompleted: () =>
      viewStateVar({
        ...viewStateVar(),
        openEditCollection: false,
        openMyCollection: true,
      }),
    refetchQueries: [showMyCollections],
  });

  const handleToggle = (): void => {
    setMenuOpen((prev) => !prev);
  };

  const handleClose = (): void => {
    setMenuOpen(false);
  };

  const clearCollection = (): void => {
    const collectionItemsId = collectionItems.map(({ node }) => node.contentObject.id);
    deleteItemsHandler({
      variables: {
        assetIds: collectionItemsId,
        collectionId: viewState.selectedCollectionId,
      },
    });
  };

  const playlistPreviewHandler = useCallback((): void => {
    const playlistNewTab = window.open(`${HOME_PATH}/collections/${viewState.selectedCollectionId}`, '_blank');
    if (playlistNewTab) {
      playlistNewTab.focus();
    }
  }, [viewState.selectedCollectionId]);

  const handleMenuItemClick = async (item: ActionItem, _index: number): Promise<void> => {
    if (item.label === SHARE) {
      viewStateVar({
        ...viewStateVar(),
        shareDialog: viewState.selectedCollectionId,
        shareContent: COLLECTION,
      });
    } else if (item.label === DOWNLOAD) {
      getDownloadLinks({
        variables: {
          globalIds: getCollectionItemGlobalIds(collectionItems),
        },
      })
        .then(({ data }) => {
          const gqlLinks = data?.downloadLinks;
          const manager = new DownloadManager(6, true);
          if (gqlLinks && gqlLinks.length > 0) {
            queueAndDownload({ downloadLinks: gqlLinks, manager, toastr: cToastr });
          } else {
            cToastr.info('No downloadable files found');
          }
        })
        .catch((error: Error) => cToastr.error(onDownloadLinkErrorMsg(error?.message)));
    } else if (item.label === DELETE) {
      deleteCollectionHandler();
      cToastr.success(`"${viewState.selectedCollectionName}" playlist was deleted successfully`);
    } else if (item.label === RENAME) {
      viewStateVar({
        ...viewStateVar(),
        renamePlaylist: viewState.selectedCollectionId,
      });
    } else if (item.label === PREVIEW) {
      playlistPreviewHandler();
    } else if (item.label === CLEAR) {
      clearCollection();
      cToastr.success(`"${viewState.selectedCollectionName}" playlist cleared`);
    }
    if (item.label === CREATE_GALLERY) {
      setOpenGalleryModal(true);
    }
    setMenuOpen(false);
  };

  if (collectionLoading || permsLoading) {
    return <CustomLinearProgress />;
  }

  if (permsError) {
    cToastr.error(`Error loading permissions: ${permsError.message}`);
    return null;
  }

  if (collectionError) {
    cToastr.error(`Error loading collection data: ${collectionError.message}`);
    return null;
  }

  return (
    <div className={classes.actionsButtonContainer} ref={anchorRef}>
      <Button
        variant="text"
        color="primary"
        size="small"
        aria-controls={menuOpen ? 'playlist-button-menu' : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-label="select playlist options"
        aria-haspopup="menu"
        onClick={handleToggle}
        id="cyp-playlist-actions-btn"
        className={classes.actionsButton}
        sx={{ padding: '2px', minWidth: 0 }}
      >
        <MoreVert sx={{ fontSize: 16 }} />
      </Button>
      <StyledContextMenu
        actions={getActions()}
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={handleClose}
        onAction={handleMenuItemClick}
        placement="left-start"
        testIdPrefix="cyp-edit-playlist-action"
        id="playlist-button-menu"
      />
      <CreateGallerySwitch
        isOpen={openGalleryModal}
        collectionId={viewState.selectedCollectionId}
        onClose={() => setOpenGalleryModal(false)}
      />
    </div>
  );
};

export default ActionsButton;
